import React, { useContext, useState, useEffect, FunctionComponent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Ui
import ModelTable from '@/ui/Table/ModelTable';
import TableButtonView from '@/ui/Table/ActionButtons/TableButtonView';
import TableButtonEdit from '@/ui/Table/ActionButtons/TableButtonEdit';
import TableButtonDelete from '../Table/ActionButtons/TableButtonDelete';
import TableButtonShowPhotos from '@/ui/Table/ActionButtons/TableButtonShowPhotos';
import TableButtonDocuments from '@/ui/Table/ActionButtons/TableButtonDocuments';
import { DialogFormGenerateFunction } from '@/ui/DataCentric/DataCentricPage';
import GeneralDialog from '@/ui/Dialog/GeneralDialog';

// Context
import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import LoaderContext, { LoaderContextType } from '@/context/LoaderContext/LoaderContext';
import SnackbarContext, { SnackbarContextType } from '@/context/SnackbarContext/SnackbarContext';

// Components
import useDocuments from '@/components/useDocuments';

// Lib
import DataController, { IDataController } from '@/lib/DataController';

// Types
import { DCRecord } from '@/@types/lib/dataController';
import { IModel } from '@/@types/models/model';
import { ClosingDetails } from '@/@types/components/formController';
import { AllowRowActionFn, RowActionFn } from '@/@types/ui/Table';
import TableButtonShow3D from '../Table/ActionButtons/TableButtonShow3D';

import TableHeaderButtonShowDeleted from '../Table/HeaderButtons/TableHeaderButtonShowDeleted';

interface IDataCentricPrimaryTableProps {
  onLoad?: (records: Array<DCRecord>) => void;
  onRecordSelect: (id: number, queryAttribute?: string | number | null) => void;
  onRowAction?: RowActionFn;
  allowRowAction?: AllowRowActionFn;

  setAllowImages: () => void;
  imagesEnabled: boolean;

  selectedRecordId?: number;
  view: string | undefined;

  baseRecordPath?: string;

  model: IModel;
  titleToken: string;

  allowAdd?: boolean;
  allowView?: boolean;
  allowEdit?: boolean;
  allowDelete?: boolean;
  allowShowImages?: boolean;
  allowExport?: boolean;
  allowExportShape?: boolean;
  allowExportDxf?: boolean;
  allowDocumentButton?: boolean;
  allowShow3D?: boolean;
  dialogComponent?: React.FunctionComponent<IDialogProps>;
  dialogForm?: string | DialogFormGenerateFunction;
  dialogAsPagePath?: string | undefined;
  allowShowDeleted?: boolean;

  triggerRefresh: boolean;
  setTriggerRefresh: (state: boolean) => void;

  queryAttribute?: string;
  tableIdentifier?: string;
  allowPrimaryModelActivate?: boolean;
  geometryChangeType?: 'LineString' | 'Polygon' | 'Point';
  mapId?: number;
  onGeomChange?: () => void;
}

const DataCentricPrimaryTable: FunctionComponent<React.PropsWithChildren<IDataCentricPrimaryTableProps>> = (props) => {
  const loaderContext = useContext(LoaderContext) as LoaderContextType;
  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;
  const { showConfirmDialog, showDialog } = useContext(DialogContext);
  const { checkIfHasGid, showDocumentsDialog } = useDocuments();
  // const { showCommentsDialog } = useComments();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { onLoad, onRowAction, allowRowAction, setAllowImages, onRecordSelect, onGeomChange } = props;
  const { model, titleToken, view, imagesEnabled, selectedRecordId, queryAttribute, tableIdentifier } = props;
  const {
    allowAdd,
    allowView,
    allowEdit,
    allowDelete,
    allowExport,
    allowExportShape,
    allowExportDxf,
    baseRecordPath,
    allowShowImages,
    dialogComponent = GeneralDialog,
    dialogForm,
    dialogAsPagePath,
    allowPrimaryModelActivate = false,
    geometryChangeType,
    mapId,
    allowDocumentButton = true,
    allowShow3D = false,
    triggerRefresh,
    setTriggerRefresh,
    allowShowDeleted,
  } = props;

  useEffect(() => {
    if (triggerRefresh) {
      setTriggerRefresh(false);
      refreshRecords();
    }
  }, [triggerRefresh]);

  /* const [entityStats, setEntityStats] = useState<IGIDStats>({
    comments: 0,
    documents: 0,
    loading: true,
  }); */

  const [records, setRecords] = useState<DCRecord[]>([]);

  const [showDeleted, setShowDeleted] = useState(false);

  const filteredRecords = useMemo(() => {
    if (!allowShowDeleted) return records;
    if (showDeleted) return records;
    return records.filter((rec) => !rec.is_deleted);
  }, [records, showDeleted]);

  const deletedHeaderButton = allowShowDeleted ? (
    <TableHeaderButtonShowDeleted showDeleted={showDeleted} onClick={() => setShowDeleted((prev) => !prev)} />
  ) : null;

  useEffect(() => {
    refreshRecords();
  }, []);

  const refreshRecords = () => {
    loaderContext.toggleLoading(true);
    dc.GetData(baseRecordPath ?? undefined)
      .then((resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setRecords(resp.data);
          if (resp.data.length === 0) {
            snackbarContext.showNotification(t('messages.nodata'), 'warning');
          }
          if (onLoad) onLoad(resp.data);
        }
      })
      // .catch((resp) => {
      //   console.log(resp);
      // })
      .finally(() => {
        loaderContext.toggleLoading(false);
      });
  };

  const dc: IDataController = new DataController(model);

  const handleView = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];

    const form = typeof dialogForm === 'function' ? dialogForm(record) : dialogForm;

    if (dialogComponent && record?.id && dialogAsPagePath === undefined) {
      showDialog(dialogComponent, {
        dc,
        mode: 'view',
        form,
        recordId: record.id,
        onClose: handleDialogClose,
        title: `${t(titleToken)} - ${t('common.view')}`,
        baseRecordPath,
        geometryChangeType,
        mapId,
      });
    } else if (record !== undefined && record?.id && dialogAsPagePath !== undefined) {
      navigate(dialogAsPagePath + String(record.id), {
        state: { view: 'view', form },
      });
    }
  };

  const handleEdit = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];

    const form = typeof dialogForm === 'function' ? dialogForm(record) : dialogForm;

    if (dialogComponent && record?.id && dialogAsPagePath === undefined) {
      showDialog(dialogComponent, {
        dc,
        mode: 'update',
        form,
        initialRecord: record,
        onClose: handleDialogClose,
        title: `${t(titleToken)} - ${t('common.update')}`,
        baseRecordPath,
        allowPrimaryModelActivate,
        geometryChangeType,
        mapId,
        onGeomChange,
        allowPrimaryDelete: allowDelete,
      });
    } else if (record !== undefined && record?.id && dialogAsPagePath !== undefined) {
      navigate(dialogAsPagePath + String(record.id), {
        state: { view: 'update', form },
      });
    }
  };

  const handleDelete = async (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const record = sel[Object.keys(sel)[0]];

    const confirmResult = await showConfirmDialog({
      title: t('cdialogs.are_you_sure') as string,
      text: t('cdialogs.once_deleted') as string,
      confirmButtonText: t('buttons.confirm') as string,
      cancelButtonText: t('buttons.cancel') as string,
    });

    if (confirmResult.success && record.id) {
      await dc.DeleteRecord(record.id as number);
      refreshRecords();
    }
  };

  const handleDocuments = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];
    const gid = record.gid as string;

    if (gid) {
      showDocumentsDialog(gid, handleDialogClose);
    }
  };

  const isDocumentsButtonEnabled: (selection: { [key: string]: DCRecord }) => boolean = (sel) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];

    if (allowShowDeleted) {
      return !!record.gid && !record.is_deleted;
    }

    return !!record.gid;
  };

  const handleAdd = () => {
    if (dialogComponent) {
      showDialog(dialogComponent, {
        dc,
        mode: 'insert',
        form: 'insert',
        onClose: handleDialogClose,
        title: `${t(titleToken)} - ${t('common.insert')}`,
        baseRecordPath,
        geometryChangeType,
        mapId,
      });
    }
  };

  const handleDialogClose = (result: ClosingDetails) => {
    if (result.dataChanged) {
      refreshRecords();
    }
  };

  /* useEffect(() => {
    if (!rowSelected?.gid) {
      setEntityStats({ comments: 0, documents: 0, loading: false });
      return;
    }

    fetchStats();
  }, [rowSelected, userContext]);

  const fetchStats = async () => {
    try {
      let resp;
      if (!rowSelected?.gid) {
        return;
      }
      if (userContext.hasAnyPermission([Permissions.Private])) {
        resp = await api.get(`core/entity-stats/${rowSelected.gid}`);
      } else if (userContext.hasAnyPermission([Permissions.PublicDocuments])) {
        resp = await api.get(`core/entity-stats-public/${rowSelected.gid}`);
      } else {
        setEntityStats({ comments: 0, documents: 0, loading: false });
        return;
      }

      if (resp.success) {
        setEntityStats({ ...(resp.data as IGIDStats), loading: false });
      } else {
        setEntityStats({ comments: 0, documents: 0, loading: false });
      }
    } catch {
      setEntityStats({ comments: 0, documents: 0, loading: false });
    }
  }; */

  const hasGid = checkIfHasGid(records);

  /* const handleComments = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];
    const gid = record.gid as string;

    if (gid && lokacijaFeature) {
      showCommentsDialog(gid, lokacijaFeature, () => {
        fetchStats();
      });
    }
  }; */

  return (
    <ModelTable
      identifier={tableIdentifier}
      title={t(titleToken)}
      records={filteredRecords}
      dc={dc}
      allowSelection="one"
      allowFilter
      allowExport={allowExport}
      allowExportShape={allowExportShape}
      allowExportDxf={allowExportDxf}
      allowAdd={allowAdd}
      allowColumnPicker
      handleAdd={handleAdd}
      addLabel={`${t('buttons.new_f')}`}
      onRowAction={onRowAction !== undefined ? onRowAction : () => {}}
      onRowClick={(evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
        const clickedRecord = sel[Object.keys(sel)[0]];
        if (queryAttribute) {
          return onRecordSelect(clickedRecord.id as number, clickedRecord[queryAttribute] as number | string | null);
        }
        return onRecordSelect(clickedRecord.id as number);
      }}
      allowRowAction={allowRowAction || (() => true)}
      viewName={view}
      rowSelectedCustom={selectedRecordId}
      customHeaderControl={deletedHeaderButton}
    >
      {allowShow3D ? <TableButtonShow3D /> : null}
      {allowDocumentButton && hasGid ? <TableButtonDocuments isEnabledRecord={isDocumentsButtonEnabled} onClick={handleDocuments} /> : null}
      {/* hasGid ? <TableButtonComments badgeValue={entityStats.comments} onClick={handleComments} disabled={!lokacijaFeature} /> : null */}
      {allowShowImages ? <TableButtonShowPhotos disabled={!imagesEnabled} variant="outlined" onClick={setAllowImages} /> : null}
      {allowDelete ? <TableButtonDelete variant="outlined" onClick={handleDelete} /> : null}
      {allowEdit ? <TableButtonEdit variant="outlined" onClick={handleEdit} /> : null}
      {allowView ? <TableButtonView variant="contained" onClick={handleView} /> : null}
    </ModelTable>
  );
};

export default DataCentricPrimaryTable;
